import { buildProductUrl, fetchVariantData as resolveVariant, isVariantFullyAvailable } from '@utilities/product';

if (!customElements.get('quick-add-to-bag')) {
  customElements.define('quick-add-to-bag', class QuickAddToBag extends HTMLElement {
    static activeInstances = new Set();

    constructor() {
      super();
      this.pendingColorOptionValueId = '';
      this.isAddingToBag = false;
      this.boundHandleParentMouseEnter = this.handleParentMouseEnter.bind(this);
      this.boundHandleParentMouseLeave = this.handleParentMouseLeave.bind(this);
      this.boundHandleParentSwatchChange = this.handleParentSwatchChange.bind(this);
      this.boundHandleOptionChange = this.handleOptionChange.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    }

    allowDebug() {
      return this.classList.contains('debug');
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.elements?.parentCard?.removeEventListener('mouseenter', this.boundHandleParentMouseEnter);
      this.elements?.parentCard?.removeEventListener('mouseleave', this.boundHandleParentMouseLeave);
      this.elements?.parentCard?.removeEventListener('product-card:swatch-change', this.boundHandleParentSwatchChange);
      this.elements?.optionSelectContainer?.removeEventListener('change', this.boundHandleOptionChange);
      this.elements?.optionSelectContainer?.removeEventListener('submit', this.boundHandleFormSubmit);
      QuickAddToBag.activeInstances.delete(this);
    }

    init() {
      this.componentActive = false;
      this.setElements();
      this.setEventListeners();
    }

    setElements() {
      this.elements = {};
      this.elements.cartMessages = this.querySelector('.quick-add-to-bag__cart-messages');
      this.elements.optionSelectContainer = this.querySelector('.quick-add-to-bag__option-select-container');
      this.elements.parentCard = this.closest('product-card');
    }

    setEventListeners() {
      this.elements.parentCard?.addEventListener('mouseenter', this.boundHandleParentMouseEnter);
      this.elements.parentCard?.addEventListener('mouseleave', this.boundHandleParentMouseLeave);
      this.elements.parentCard?.addEventListener('product-card:swatch-change', this.boundHandleParentSwatchChange);
      this.elements.optionSelectContainer?.addEventListener('change', this.boundHandleOptionChange);
      this.elements.optionSelectContainer?.addEventListener('submit', this.boundHandleFormSubmit);
    }

    static closeAllOthers(exceptInstance = null) {
      QuickAddToBag.activeInstances.forEach((instance) => {
        if (instance !== exceptInstance && instance.isActive()) {
          instance.close();
        }
      });
    }

    async handleParentMouseEnter() {
      await this.open();
    }

    handleParentMouseLeave() {
      this.close();
    }

    async handleParentSwatchChange(event) {
      const swatchData = event?.detail?.swatchData || {};
      const optionValueId = swatchData?.option_value_id
        ? String(swatchData.option_value_id)
        : '';

      if (optionValueId) {
        this.pendingColorOptionValueId = optionValueId;
      }

      if (!this.elements.optionSelectContainer?.children?.length) return;

      this.syncHiddenColorSelection(optionValueId);
      this.clearSelectionsExceptColor();
      await this.resolveVariantFromSelections({
        baseUrl: swatchData?.url || this.getActiveProductUrl(),
        autoAdd: false,
        skipOptionRefresh: true,
      });
    }

    async handleOptionChange(event) {
      if (this.isAddingToBag) return;
      const changedInput = event.target?.closest('.js-variant-option');
      if (!changedInput) return;
      if (changedInput.disabled) return;
      if (changedInput.classList.contains('option-impossible')) return;
      if (changedInput.classList.contains('option-unavailable')) return;

      this.clearCartMessage();
      const quickAddMode = this.getQuickAddMode();
      const shouldAutoAdd = quickAddMode === 'auto' && this.hasAllOptionsSelected();
      await this.resolveVariantFromSelections({
        baseUrl: this.getActiveProductUrl(),
        autoAdd: shouldAutoAdd,
        skipOptionRefresh: true,
      });
    }

    async handleFormSubmit(event) {
      if (this.isAddingToBag) return;
      const form = event.target?.closest('form.quick-add-to-bag__form');
      if (!form) return;

      event.preventDefault();
      this.clearCartMessage();

      const selectedOptionValueIds = this.getSelectedOptionValueIds();
      if (selectedOptionValueIds.length === 0) return;

      const hiddenVariantId = String(
        this.elements.optionSelectContainer?.querySelector('input[name="id"]')?.value || '',
      );
      const parentVariantId = String(this.elements.parentCard?.getVariant?.()?.id || '');
      const alreadyResolved = this.hasAllOptionsSelected()
        && !!hiddenVariantId
        && hiddenVariantId === parentVariantId;
      if (alreadyResolved) {
        await this.addCurrentVariantToCart();
        return;
      }

      const variantData = await this.resolveVariantFromSelections({
        baseUrl: this.getActiveProductUrl(),
        optionValueIds: selectedOptionValueIds,
        autoAdd: false,
        skipOptionRefresh: true,
      });

      if (!isVariantFullyAvailable(variantData?.selected_variant)) return;
      await this.addCurrentVariantToCart();
    }

    async open() {
      if (!this.elements.parentCard) return;

      // Reset any stale loading/error UI from prior interactions before re-rendering.
      this.setAddingToBagState(false);
      this.clearCartMessage();
      QuickAddToBag.closeAllOthers(this);

      const successfulInjection = await this.injectProductOptions();
      if (successfulInjection) {
        this.classList.add('active');
        this.componentActive = true;
        QuickAddToBag.activeInstances.add(this);
      }
    }

    close() {
      // Ensure hover exit always restores default visibility state.
      this.setAddingToBagState(false);
      this.clearCartMessage();
      this.classList.remove('active');
      this.componentActive = false;
      QuickAddToBag.activeInstances.delete(this);
    }

    isActive() {
      return this.componentActive;
    }

    clearCartMessage() {
      if (!this.elements.cartMessages) return;
      this.elements.cartMessages.innerHTML = '';
    }

    setCartMessage(message) {
      if (!message || message.trim().length === 0) return;
      if (!this.elements.cartMessages) return;
      this.elements.cartMessages.innerHTML = `<p class="quick-add-to-bag__cart-error">${message}</p>`;
    }

    setAddingToBagState(isAdding) {
      this.isAddingToBag = !!isAdding;
      this.classList.toggle('quick-add-to-bag--adding', this.isAddingToBag);
      this.toggleAttribute('aria-busy', this.isAddingToBag);

      const submitButton = this.elements.optionSelectContainer?.querySelector('.quick-add-to-bag__add-to-bag');
      if (submitButton) {
        if (this.isAddingToBag) {
          submitButton.dataset.wasDisabledBeforeAdding = String(submitButton.hasAttribute('disabled'));
          submitButton.setAttribute('disabled', 'true');
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        } else {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          if (submitButton.dataset.wasDisabledBeforeAdding === 'true') {
            submitButton.setAttribute('disabled', 'true');
          } else {
            submitButton.removeAttribute('disabled');
          }
          delete submitButton.dataset.wasDisabledBeforeAdding;
        }
      }

      if (this.isAddingToBag) {
        this.elements.cartMessages.innerHTML = '<p class="quick-add-to-bag__cart-status" data-adding-status>Adding to bag...</p>';
      } else {
        this.elements.cartMessages
          ?.querySelector('[data-adding-status]')
          ?.remove();
      }
    }

    async addCurrentVariantToCart() {
      if (this.isAddingToBag) return;
      this.setAddingToBagState(true);
      try {
        await this.elements.parentCard?.handleAddToCart?.();
      } finally {
        this.setAddingToBagState(false);
      }
    }

    getActiveProductUrl() {
      const parentCard = this.elements.parentCard;
      if (!parentCard) return window.location.pathname;
      return parentCard.getActiveProductUrl();
    }

    getQuickAddMode() {
      const mode = String(
        this.dataset.quickAddMode
        || this.elements.parentCard?.dataset?.quickAddMode
        || window.theme?.settings?.quick_add_mode
        || 'auto',
      ).toLowerCase();
      return mode === 'manual' ? 'manual' : 'auto';
    }
    isMultiOptionProduct() {
      const optionCount = this.elements.parentCard?.product?.options?.length || 0;
      return optionCount > 1;
    }

    getHiddenColorOptionValueId(selectedSwatchData = {}) {
      const scopedColorOptionValueId = this.pendingColorOptionValueId
        || String(selectedSwatchData?.option_value_id || '');
      return scopedColorOptionValueId || '';
    }

    shouldUseColorOnlyInitialization(selectedSwatchData = {}) {
      return this.isMultiOptionProduct() && !!this.getHiddenColorOptionValueId(selectedSwatchData);
    }

    /**
     * Multi-option products should initialize from hidden color only so visible groups
     * (size/inseam/etc.) remain unselected until shopper interaction.
     */
    getScopedOptionValueIdsForInject(selectedSwatchData = {}) {
      const scopedColorOptionValueId = this.getHiddenColorOptionValueId(selectedSwatchData);
      if (this.shouldUseColorOnlyInitialization(selectedSwatchData)) {
        return [scopedColorOptionValueId];
      }

      const parent = this.elements.parentCard;
      if (typeof parent?.getResolvedOptionValueIdsForQuickAdd === 'function') {
        const fromProduct = parent.getResolvedOptionValueIdsForQuickAdd();
        if (fromProduct.length > 0) {
          return fromProduct;
        }
      }
      return scopedColorOptionValueId ? [scopedColorOptionValueId] : [];
    }

    async injectProductOptions() {
      const selectedSwatchData = this.elements.parentCard?.getSelectedSwatchData?.() || {};
      const fetchUrl = selectedSwatchData?.url || this.getActiveProductUrl();
      if (!fetchUrl) return false;
      const scopedOptionValueIds = this.getScopedOptionValueIdsForInject(selectedSwatchData);

      try {
        if (this.allowDebug()) {
          console.log(`Injecting product options for ${this.elements.parentCard.product.title}`, this, selectedSwatchData);
        }

        const injected = await this.getProductOptionData(fetchUrl, scopedOptionValueIds);
        if (!injected) return false;

        this.syncHiddenColorSelection(this.getHiddenColorOptionValueId(selectedSwatchData));
        if (this.shouldUseColorOnlyInitialization(selectedSwatchData)) {
          this.clearSelectionsExceptColor();
        }
        await this.resolveVariantFromSelections({
          baseUrl: fetchUrl,
          autoAdd: false,
          skipOptionRefresh: true,
        });
        return true;
      } catch (error) {
        console.error(error);
      }

      return false;
    }

    async getProductOptionData(productFetchUrl, optionValueIds = []) {
      if (!productFetchUrl) return false;
      const variantData = await this.fetchProductOptionData(productFetchUrl, optionValueIds);
      if (!variantData) return false;

      if (this.allowDebug()) {
        console.log(`Product options HTML pulled for ${this.elements.parentCard.product.title}`);
      }

      const html = new DOMParser().parseFromString(variantData, 'text/html');
      const templateRoot = html.querySelector('.template-container');
      return this.updateProductOptions(templateRoot);
    }

    async fetchProductOptionData(productFetchUrl, optionValueIds = []) {
      if (!productFetchUrl) return false;

      try {
        const fetchUrl = buildProductUrl(productFetchUrl, optionValueIds, {
          view: 'product-card-add-to-bag-options',
        });
        if (!fetchUrl) return false;
        const response = await fetch(fetchUrl);
        if (!response.ok) return false;
        const text = await response.text();
        this._lastQuickAddOptionsFetchUrl = fetchUrl;
        return text;
      } catch (error) {
        console.error(error);
      }

      return false;
    }

    updateProductOptions(html) {
      if (!html || !this.elements.optionSelectContainer) return false;
      const markup = html.innerHTML?.trim() || '';
      if (!markup) return false;
      this.elements.optionSelectContainer.innerHTML = markup;
      this.updateProductOptionsFromData(this.elements.parentCard?.product?.options || []);
      this.updateHiddenVariantId(this.elements.parentCard?.getVariant?.()?.id || '');
      return true;
    }

    getSelectedOptionValueIds() {
      const groups = [...(this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [])];
      if (groups.length === 0) return [];

      const sortedGroups = groups.sort((a, b) => {
        const aKey = Number(String(a.dataset.optionKey || '').replace('option', '')) || Number.MAX_SAFE_INTEGER;
        const bKey = Number(String(b.dataset.optionKey || '').replace('option', '')) || Number.MAX_SAFE_INTEGER;
        return aKey - bKey;
      });

      return sortedGroups
        .map((group) => group.querySelector('input[type="radio"]:checked')?.dataset?.optionValueId || '')
        .filter(Boolean)
        .map((valueId) => String(valueId));
    }

    getSelectionsByOptionKey() {
      const selections = {};
      const groups = this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [];
      groups.forEach((group) => {
        const optionKey = group.dataset.optionKey;
        const selected = group.querySelector('input[type="radio"]:checked');
        const selectedOptionValueId = selected?.dataset?.optionValueId;
        if (optionKey && selectedOptionValueId) {
          selections[optionKey] = String(selectedOptionValueId);
        }
      });
      return selections;
    }

    getOptionValueIdsFromSelectionsByOptionKey(selections = {}) {
      const groups = [...(this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [])];
      if (groups.length === 0) return [];

      const sortedGroups = groups.sort((a, b) => {
        const aKey = Number(String(a.dataset.optionKey || '').replace('option', '')) || Number.MAX_SAFE_INTEGER;
        const bKey = Number(String(b.dataset.optionKey || '').replace('option', '')) || Number.MAX_SAFE_INTEGER;
        return aKey - bKey;
      });

      return sortedGroups
        .map((group) => String(group.dataset.optionKey || ''))
        .map((optionKey) => selections[optionKey] || '')
        .filter(Boolean)
        .map((valueId) => String(valueId));
    }

    restoreSelectionsByOptionKey(selections = {}) {
      if (!selections || typeof selections !== 'object') return;
      const groups = this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [];
      groups.forEach((group) => {
        const radioInputs = group.querySelectorAll('input[type="radio"]');
        radioInputs.forEach((radio) => {
          radio.checked = false;
        });

        const optionKey = group.dataset.optionKey;
        const optionValueId = optionKey ? selections[optionKey] : null;
        if (!optionValueId) return;

        const targetInput = group.querySelector(
          `.js-variant-option[data-option-value-id="${optionValueId}"]`,
        );
        if (!targetInput || targetInput.disabled) return;
        targetInput.checked = true;
      });
    }

    async refreshOptionViewForVariant(variantUrl, selections = {}, optionValueIds = []) {
      if (!variantUrl) return;
      const scopedOptionValueIds = optionValueIds.length > 0
        ? optionValueIds
        : this.getOptionValueIdsFromSelectionsByOptionKey(selections);
      const nextOptionsUrl = buildProductUrl(variantUrl, scopedOptionValueIds, {
        view: 'product-card-add-to-bag-options',
      });
      if (
        nextOptionsUrl
        && this._lastQuickAddOptionsFetchUrl
        && nextOptionsUrl === this._lastQuickAddOptionsFetchUrl
      ) {
        this.restoreSelectionsByOptionKey(selections);
        return;
      }
      const variantHtml = await this.fetchProductOptionData(variantUrl, scopedOptionValueIds);
      if (!variantHtml) return;
      const html = new DOMParser().parseFromString(variantHtml, 'text/html');
      const templateRoot = html.querySelector('.template-container');
      const ok = this.updateProductOptions(templateRoot);
      if (!ok) return;
      this.restoreSelectionsByOptionKey(selections);
    }

    syncHiddenColorSelection(optionValueId) {
      if (!optionValueId || !this.elements.optionSelectContainer) return;

      const colorOptionGroup = this.elements.optionSelectContainer.querySelector('.quick-add-to-bag__option-group--color');
      if (!colorOptionGroup) return;

      const colorInput = colorOptionGroup.querySelector(
        `.js-variant-option[data-option-value-id="${optionValueId}"]`,
      );
      if (!colorInput) return;

      colorInput.checked = true;
      this.pendingColorOptionValueId = String(optionValueId);
    }

    clearSelectionsExceptColor() {
      if (!this.elements.optionSelectContainer) return;

      const optionGroups = this.elements.optionSelectContainer.querySelectorAll('.product-card__option');
      optionGroups.forEach((group) => {
        if (group.classList.contains('quick-add-to-bag__option-group--color')) return;

        const radioInputs = group.querySelectorAll('input[type="radio"]');
        radioInputs.forEach((radio) => {
          radio.checked = false;
        });
      });
    }

    updateHiddenVariantId(variantId) {
      const idInput = this.elements.optionSelectContainer?.querySelector('input[name="id"]');
      if (!idInput || !variantId) return;
      idInput.value = String(variantId);
    }

    async resolveVariantFromSelections({
      baseUrl = null,
      autoAdd = false,
      optionValueIds: incomingOptionValueIds = [],
      skipOptionRefresh = false,
    } = {}) {
      const selectedOptionValueIds = incomingOptionValueIds.length > 0
        ? incomingOptionValueIds.map((valueId) => String(valueId))
        : this.getSelectedOptionValueIds();
      if (selectedOptionValueIds.length === 0) return null;
      const currentSelections = this.getSelectionsByOptionKey();

      const variantData = await resolveVariant(baseUrl || this.getActiveProductUrl(), selectedOptionValueIds);
      if (!variantData) return null;

      if (!skipOptionRefresh) {
        await this.refreshOptionViewForVariant(
          variantData?.url || baseUrl || this.getActiveProductUrl(),
          currentSelections,
          selectedOptionValueIds,
        );
      }

      this.updateProductOptionsFromData(variantData.options || []);
      this.updateHiddenVariantId(variantData?.selected_variant?.id || '');

      this.dispatchEvent(new CustomEvent('quick-add-to-bag:variant-change', {
        bubbles: true,
        detail: {
          triggeringElement: this,
          variantData,
          optionValueIds: selectedOptionValueIds,
        },
      }));

      if (autoAdd && this.hasAllOptionsSelected() && isVariantFullyAvailable(variantData?.selected_variant)) {
        await this.addCurrentVariantToCart();
      }

      return variantData;
    }

    updateProductOptionsFromData(optionsData = []) {
      if (this.allowDebug()) {
        console.log(`Updating product options from option JSON for ${this.elements.parentCard.product.title}`, optionsData);
      }

      this.clearCartMessage();
      if (!Array.isArray(optionsData)) return;

      // A rendered color always has at least one available variant, and its
      // combo-aware `available` value resolves against the *default* values of
      // the other options. Defer color cross-out until every other option level
      // is selected so we don't prematurely cross out otherwise-available colors.
      const otherOptionsSelected = this.areNonColorOptionsSelected();

      for (const option of optionsData) {
        if (!option?.values || !Array.isArray(option.values)) continue;

        const isColorOption = option.is_color_option === true;

        for (const value of option.values) {
          const optionValueId = String(value.id);
          const matchingInputs = this.elements.optionSelectContainer?.querySelectorAll(
            `.js-variant-option[data-option-value-id="${optionValueId}"]`,
          ) || [];

          matchingInputs.forEach((input) => {
            const isImpossible = value.variant === null;
            let isUnavailable = value.available === false;

            if (isColorOption && !otherOptionsSelected) {
              isUnavailable = false;
            }

            this.updateOptionClasses(input, isImpossible, isUnavailable);
            input.dataset.optionAvailable = String(!isImpossible && !isUnavailable);
            input.dataset.optionVariant = value.variant !== null ? String(value.variant) : '';
          });
        }
      }
    }

    /**
     * Returns true when every non-color option group has a checked value.
     * Used to defer color cross-out until the combination is complete.
     */
    areNonColorOptionsSelected() {
      const groups = this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [];
      if (groups.length === 0) return true;

      return [...groups].every((group) => {
        if (group.classList.contains('quick-add-to-bag__option-group--color')) return true;
        return !!group.querySelector('input[type="radio"]:checked');
      });
    }

    updateOptionClasses(input, impossible, unavailable) {
      if (!input) return;
      input.classList.toggle('option-impossible', impossible);
      input.classList.toggle('option-unavailable', unavailable && !impossible);
      input.disabled = impossible || unavailable;
      if (impossible) {
        input.setAttribute('tabindex', '-1');
      } else {
        input.removeAttribute('tabindex');
      }
    }

    hasAllOptionsSelected() {
      const groups = this.elements.optionSelectContainer?.querySelectorAll('.product-card__option') || [];
      if (groups.length === 0) return false;
      return [...groups].every((group) => !!group.querySelector('input[type="radio"]:checked'));
    }

    clearSelections() {
      if (!this.elements.optionSelectContainer) return;

      const optionGroups = this.elements.optionSelectContainer.querySelectorAll('.product-card__option');
      optionGroups.forEach((group) => {
        const radioInputs = group.querySelectorAll('input[type="radio"]');
        radioInputs.forEach((radio) => {
          radio.checked = false;
        });
      });

      this.clearCartMessage();
    }
  });
}
